import * as React from 'react';
import { Box, Flex, FormLabel, Input, InputGroup, InputLeftElement, Image, FormErrorMessage } from '@chakra-ui/react';
import { PhoneNumberUtil } from 'google-libphonenumber';
import debounce from 'lodash/debounce';

import { withConfirm } from '../withConfirm';
import { UndoEdit } from '../../iconButtons/UndoEdit';

import flags from '../../../assets/flags/flags.png';
import blank from '../../../assets/flags/blank.gif';
import '../../../assets/flags/flags.scss';
import phone from './phone.json';
import names from './names.json';
import {
  getPhoneNumber,
  isCaribbeanFormatPossiblePhoneNumberValid,
  isCountryCodeValid
} from '../../../utilities/validation';
import { CustomFormControl, DirectionProp } from '../CustomFormControl';

export interface IProps extends DirectionProp {
  id?: string;
  codeValue?: string;
  numberValue?: string;
  label?: string;
  phoneCodeName?: string;
  phoneNumberName?: string;
  subText?: string;
  countryCodeLabel?: string;
  phoneLabel?: string;
  inputHelperText?: string | React.ReactNode;
  errorTextCode?: string;
  errorTextNumber?: string;
  forwardedRef?: ((instance: HTMLInputElement | null) => void) | React.RefObject<HTMLInputElement> | null | undefined;
  onChangeCode?: (event: React.FormEvent<HTMLInputElement>) => void;
  onChangeNumber?: (event: React.FormEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FormEvent<HTMLInputElement>) => void;
  onFocus?: (event: React.FormEvent<HTMLInputElement>) => void;
  onValidationFailed?: (error: IValidationError) => void;
  onValidationSuccess?: () => void;
  canUndo?: boolean;
  onUndo?: () => void;
  size?: 'lg' | 'md' | 'sm';
  mb?: number | string;
  isDisabled?: boolean;
  /**
   * When true, uses the errorTextCode and errorTextNumber props to display errors
   * instead of relying on a parent CustomFormControl component
   */
  isErrorPropsDirect?: boolean;
}

export interface IValidationError {
  isCodeValid: boolean;
  isNumberValid: boolean;
  codeValidationErrorMessage: string;
  numberValidationErrorMessage: string;
}

interface IState extends IValidationError {
  countryName: string;
  countryCode: string;
}

class PhoneNumberInput extends React.Component<IProps, IState> {
  _isMounted: boolean;

  constructor(props: IProps & IState) {
    super(props);
    const countryCode = this.getCountryCode(props.codeValue);
    this.state = {
      countryCode,
      countryName: this.getCountryName(countryCode),
      isCodeValid: true,
      isNumberValid: true,
      codeValidationErrorMessage: '',
      numberValidationErrorMessage: ''
    };
    this.onCodeKeyPressDebounce = debounce(this.onCodeKeyPressDebounce.bind(this), 300);
    this.validate = debounce(this.validate.bind(this), 300);
    this._isMounted = false;
  }

  componentDidMount() {
    this._isMounted = true;
    this.validate();
  }

  componentDidUpdate(prevProps: Readonly<IProps>, _prevState: Readonly<IState>, _snapshot?: any): void {
    const { codeValue, numberValue } = this.props;
    if (codeValue !== prevProps.codeValue) {
      const countryCode = this.getCountryCode(codeValue);
      const countryName = this.getCountryName(countryCode);
      this.setState({
        countryCode,
        countryName
      });
    }
    if (codeValue !== prevProps.codeValue || numberValue !== prevProps.numberValue) {
      this.validate();
    }
  }

  componentWillUnmount = () => {
    this._isMounted = false;
  };

  onCodeKeyPress = (evt: any) => {
    evt.persist();
    this.onCodeKeyPressDebounce(evt);
  };

  onCodeKeyPressDebounce(evt: any) {
    const countryCodeValue = evt.target.value;

    if (countryCodeValue !== undefined) {
      const countryCode = this.getCountryCode(countryCodeValue);
      this.setState({
        countryCode,
        countryName: this.getCountryName(countryCode)
      });
    }
  }

  getCountryCode = (codeValue = ''): string => {
    if (codeValue === '' || !codeValue) {
      return '';
    }
    let countryCode = '';
    Reflect.ownKeys(phone).forEach((code: any) => {
      // @ts-ignore
      if (phone[code] === codeValue) {
        countryCode = code ? code.toString() : '';
        //@ts-ignore
      } else if (phone[code].includes('and')) {
        // using includes as there is a code with "DO": "1809 and 1829"
        //@ts-ignore
        const codes = phone[code].split(' and ');
        if (codes[0] === codeValue || codes[1] === codeValue) {
          countryCode = code ? code.toString() : '';
        }
      }
    });
    return countryCode ? countryCode : '';
  };

  getCountryName = (countryCode = ''): string => {
    if (countryCode === '' || !countryCode) {
      return '';
    }
    let foundCountryName = '';
    Reflect.ownKeys(names).forEach((countryNameKey: any) => {
      if (countryNameKey === countryCode) {
        //@ts-ignore
        foundCountryName = names[countryNameKey];
      }
    });
    return foundCountryName || '';
  };

  validate = () => {
    const { onValidationSuccess = () => undefined } = this.props;
    const { codeValue: code, numberValue: phone } = this.props;
    if (this._isMounted && code && phone) {
      if (!isCountryCodeValid(code, phone)) {
        this.setInvalidCountryCodeError();
        return;
      }

      const util = PhoneNumberUtil.getInstance();
      const phoneNum = getPhoneNumber(code, phone);
      switch (util.isPossibleNumberWithReason(phoneNum)) {
        case PhoneNumberUtil.ValidationResult.INVALID_COUNTRY_CODE:
          this.setInvalidCountryCodeError();
          break;
        case PhoneNumberUtil.ValidationResult.TOO_LONG:
          this.setTooLongPhoneNumberError();
          break;
        case PhoneNumberUtil.ValidationResult.TOO_SHORT:
          this.setTooShortPhoneNumberError();
          break;
        case 4:
        case PhoneNumberUtil.ValidationResult.IS_POSSIBLE:
          if (util.isValidNumber(phoneNum) || isCaribbeanFormatPossiblePhoneNumberValid(code, phone)) {
            this.unsetAllErrors();
            onValidationSuccess();
          } else {
            this.setInvalidPhoneNumberError();
          }
          break;
      }
    }
  };

  setInvalidCountryCodeError = (): void => {
    const { onValidationFailed = () => undefined } = this.props;
    const newValidationState: IValidationError = {
      isCodeValid: false,
      isNumberValid: true,
      codeValidationErrorMessage: 'Invalid Country Code',
      numberValidationErrorMessage: ''
    };
    this.setState(newValidationState);
    onValidationFailed(newValidationState);
  };

  setTooLongPhoneNumberError = (): void => {
    const { onValidationFailed = () => undefined } = this.props;
    const newValidationState: IValidationError = {
      isCodeValid: true,
      isNumberValid: false,
      codeValidationErrorMessage: '',
      numberValidationErrorMessage: 'Your phone number is too long'
    };
    this.setState(newValidationState);
    onValidationFailed(newValidationState);
  };

  setTooShortPhoneNumberError = (): void => {
    const { onValidationFailed = () => undefined } = this.props;
    const newValidationState: IValidationError = {
      isCodeValid: true,
      isNumberValid: false,
      codeValidationErrorMessage: '',
      numberValidationErrorMessage: 'Your phone number is too short'
    };
    this.setState(newValidationState);
    onValidationFailed(newValidationState);
  };

  setInvalidPhoneNumberError = (): void => {
    const { onValidationFailed = () => undefined } = this.props;
    const newValidationState: IValidationError = {
      isCodeValid: true,
      isNumberValid: false,
      codeValidationErrorMessage: '',
      numberValidationErrorMessage: 'Your phone number is invalid'
    };
    this.setState(newValidationState);
    onValidationFailed(newValidationState);
  };

  unsetAllErrors = (): void => {
    this.setState({
      isCodeValid: true,
      isNumberValid: true,
      codeValidationErrorMessage: '',
      numberValidationErrorMessage: ''
    });
  };

  onChangeCode = (event: React.FormEvent<HTMLInputElement>): void => {
    const { onChangeCode = () => undefined } = this.props;
    onChangeCode(event);
  };

  onChangeNumber = (event: React.FormEvent<HTMLInputElement>): void => {
    const { onChangeNumber = () => undefined } = this.props;
    onChangeNumber(event);
  };

  onBlur = (event: React.FormEvent<HTMLInputElement>): void => {
    const { onBlur = () => undefined } = this.props;
    this.validate();
    onBlur(event);
  };

  onFocus = (event: React.FormEvent<HTMLInputElement>): void => {
    const { onFocus = () => undefined } = this.props;
    this.setState({
      codeValidationErrorMessage: '',
      numberValidationErrorMessage: ''
    });
    onFocus(event);
  };

  render() {
    const {
      id,
      label,
      phoneCodeName,
      phoneNumberName,
      countryCodeLabel,
      codeValue,
      numberValue,
      phoneLabel,
      canUndo,
      onUndo,
      subText,
      forwardedRef,
      errorTextCode,
      errorTextNumber,
      size = 'lg',
      mb = 0,
      direction,
      isDisabled = false,
      isErrorPropsDirect: isErrorPropsDirect = false
    } = this.props;
    const dataId = id || 'phone-number-default';

    // Combine all possible error messages
    const hasError =
      this.state.codeValidationErrorMessage ||
      errorTextCode ||
      this.state.numberValidationErrorMessage ||
      errorTextNumber;

    // Determine which inputs should show as invalid
    const isCodeInputInvalid = !!this.state.codeValidationErrorMessage || !!errorTextCode;
    const isNumberInputInvalid = !!this.state.numberValidationErrorMessage || !!errorTextNumber;

    return (
      <CustomFormControl
        id={dataId}
        controlProps={{
          isInvalid: !!hasError,
          mb
        }}
        direction={direction}
        size={size}
        label={label}
        labelProps={{
          htmlFor: `${dataId}-code-input-field`
        }}
        subLabel={subText}
        error={isErrorPropsDirect ? undefined : hasError}
        forwardedRef={forwardedRef}
      >
        <Flex gap={{ base: 2, lg: 4 }}>
          <Flex flexDirection="column" flexGrow="0" width={{ base: '6rem', lg: 'auto' }} gap={1}>
            <InputGroup size={size}>
              <InputLeftElement pointerEvents="none" width="auto" px={{ base: 3, lg: 4 }} color="muted">
                {this.state.countryCode ? (
                  <Image
                    data-selector={`${dataId}-flag`}
                    // @ts-ignore
                    src={blank}
                    className={`flag flag-${this.state.countryCode.toLowerCase()}`}
                    alt={this.state.countryName}
                    title={this.state.countryName}
                    bgImage={`url(${flags})`}
                    bgRepeat="no-repeat"
                    width="16px"
                    height="11px"
                    display={{ base: 'none', lg: 'block' }}
                  />
                ) : (
                  <Box
                    data-selector={`${dataId}-flag-placeholder`}
                    bgColor="chakra-subtle-bg"
                    borderWidth={1}
                    width="16px"
                    height="11px"
                    display={{ base: 'none', lg: 'block' }}
                  />
                )}

                <Box ml={{ base: 0, lg: 2 }}>+</Box>
              </InputLeftElement>

              <Input
                data-selector={`${dataId}-code-input-field`}
                id={`${dataId}-code-input-field`}
                name={phoneCodeName}
                onChange={this.onChangeCode}
                type="tel"
                inputMode="numeric"
                maxLength={3}
                autoComplete="off"
                value={codeValue}
                isInvalid={isCodeInputInvalid}
                onBlur={this.onBlur}
                onFocus={this.onFocus}
                pl={{ base: 6, lg: 14 }}
                disabled={isDisabled}
              />
            </InputGroup>

            <FormLabel
              data-selector={`${dataId}-country-code-label`}
              htmlFor={`${dataId}-code-input-field`}
              fontSize="sm"
              fontWeight="regular"
            >
              {countryCodeLabel ? countryCodeLabel : 'Country code'}
            </FormLabel>
          </Flex>

          <Flex flexDirection="column" flexGrow="1" width="100%" gap={1}>
            <InputGroup size={size}>
              <InputLeftElement pointerEvents="none" width="auto" px={{ base: 3, lg: 4 }} color="muted">
                (0)
              </InputLeftElement>

              <Input
                data-selector={`${dataId}-number-input-field`}
                id={`${dataId}-number-input-field`}
                name={phoneNumberName}
                onChange={this.onChangeNumber}
                type="tel"
                autoComplete="tel-national"
                value={numberValue}
                isInvalid={isNumberInputInvalid}
                onBlur={this.onBlur}
                onFocus={this.onFocus}
                size={size}
                pl={{ base: 10, lg: 12 }}
                disabled={isDisabled}
              />
            </InputGroup>

            <FormLabel
              data-selector={`${dataId}-number-label`}
              htmlFor={`${dataId}-number-input-field`}
              fontSize="sm"
              fontWeight="regular"
            >
              {phoneLabel ? phoneLabel : 'Phone number'}
            </FormLabel>
          </Flex>

          {canUndo && <UndoEdit id={dataId} onUndo={onUndo} />}
        </Flex>
        {/* Display a custom error message when isErrorPropsDirect is true */}
        {isErrorPropsDirect && hasError && (
          <FormErrorMessage data-selector={`${dataId}-code-error-text`}>
            {this.state.codeValidationErrorMessage ||
              errorTextCode ||
              this.state.numberValidationErrorMessage ||
              errorTextNumber}
          </FormErrorMessage>
        )}
      </CustomFormControl>
    );
  }
}

const PhoneNumberInputWithConfirm = withConfirm(PhoneNumberInput);

export { PhoneNumberInput, PhoneNumberInputWithConfirm };
